import React, {useEffect, useState} from 'react';
import Card from '../../../../Presentation/components/ui/Card';
import {AppText, AppTitle, AppSubtitle, AppSmallText, AppSectionTitle, AppCardTitle} from '../../../../Presentation/components/ui/Typography';
import {Badge} from '../../../../Presentation/components/ui/Badge';
import {useUserManagement} from '../../hooks/useUserManagement';
import {useRoles} from '../../hooks/useRoles';
import {UserStatistics as _UserStats} from '../../models/user.model';
import {
  Users,
  UserCheck,
  UserX,
  UserMinus,
  TrendingUp,
  Clock,
  RefreshCw
} from 'lucide-react';

interface UserStatisticsProps {
  className?: string;
}

export const UserStatistics: React.FC<UserStatisticsProps> = ({className = ''}) => {
  const {statistics, loadStatistics, statisticsLoading} = useUserManagement();
  const {roles} = useRoles();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Fetching user statistics
    loadStatistics();
  }, [loadStatistics]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadStatistics();
    } finally {
      setRefreshing(false);
    }
  };

  if (statisticsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <AppCardTitle>إحصائيات المستخدمين</AppCardTitle>
          <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <AppText className="text-gray-500">لا توجد إحصائيات متاحة</AppText>
        <button
          onClick={handleRefresh}
          className="mt-4 text-brand-600 hover:text-brand-800"
        >
          محاولة مرة أخرى
        </button>
      </div>
    );
  }

  const stats = [
    {
      title: 'إجمالي المستخدمين',
      value: statistics.totalUsers || 0,
      icon: <Users className="w-6 h-6" />,
      color: 'text-brand-600',
      bgColor: 'bg-brand-100',
      description: 'جميع المستخدمين المسجلين'
    },
    {
      title: 'المستخدمون النشطون',
      value: statistics.activeUsers || 0,
      icon: <UserCheck className="w-6 h-6" />,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      description: 'المستخدمون الذين يمكنهم الدخول'
    },
    {
      title: 'المستخدمون غير النشطين',
      value: statistics.inactiveUsers || 0,
      icon: <UserX className="w-6 h-6" />,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      description: 'المستخدمون المعطلون'
    },
    {
      title: 'مسوقون السوق',
      value: statistics.marketDevelopers || 0,
      icon: <UserMinus className="w-6 h-6" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      description: 'مسوقون السوق المسجلين'
    }
  ];

  const getRoleDisplayName = (roleName: string): string => {
    // Use dynamic role data from API instead of hardcoded mappings
    const role = roles.find(r => r.name === roleName);
    return role?.displayName || role?.name || roleName;
  };

  return (
    <div className={`space-y-6 flex-1 min-h-[calc(100vh-200px)] flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <AppTitle>إحصائيات المستخدمين</AppTitle>
          <AppText className="text-gray-600 leading-relaxed">
            نظرة عامة على حالة المستخدمين في النظام
          </AppText>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-3 px-4 py-2 text-brand-600 hover:text-brand-800 disabled:opacity-50 font-medium"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          <span>تحديث</span>
        </button>
      </div>

      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="p-8 hover:shadow-lg transition-all duration-300 rounded-xl border border-gray-100 bg-white">
            <div className="flex items-center justify-between">
              <div className="space-y-4 flex-1 min-w-0 pr-6">
                <AppSmallText className="font-semibold text-gray-600 uppercase tracking-wide">
                  {stat.title}
                </AppSmallText>
                <AppTitle className="text-gray-900 leading-none whitespace-nowrap overflow-hidden">
                  {stat.value.toLocaleString()}
                </AppTitle>
                <AppSmallText className="text-gray-500 leading-relaxed">
                  {stat.description}
                </AppSmallText>
              </div>
              <div className={`p-4 rounded-full ${stat.bgColor} flex-shrink-0`}>
                <div className={`${stat.color} w-6 h-6`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Percentage */}
        <Card className="p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div className="space-y-1">
                <AppCardTitle className="text-gray-900">نسبة النشاط</AppCardTitle>
                <AppSmallText className="text-gray-600">
                  المستخدمون النشطون
                </AppSmallText>
              </div>
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-3 whitespace-nowrap">
            {statistics.activePercentage || 0}%
          </div>
          <AppSmallText className="text-gray-500">
            من إجمالي المستخدمين نشط
          </AppSmallText>
        </Card>

        {/* Users by Role */}
        <Card className="p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-brand-100 rounded-full flex-shrink-0">
                <Clock className="w-5 h-5 text-brand-600" />
              </div>
              <div className="space-y-1">
                <AppCardTitle className="text-gray-900">توزيع المستخدمين حسب الدور</AppCardTitle>
                <AppSmallText className="text-gray-600">
                  إجمالي: {statistics.totalUsers || 0} مستخدم
                </AppSmallText>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {(statistics.usersByRole || []).map((roleData: any) => (
              <div key={roleData.roleName} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <Badge variant="secondary" className="text-sm font-medium px-3 py-1">
                    {getRoleDisplayName(roleData.roleName)}
                  </Badge>
                </div>
                <div className="flex items-center space-x-3">
                  <AppSectionTitle className="font-bold text-gray-900 whitespace-nowrap">
                    {roleData.count}
                  </AppSectionTitle>
                  <div className="w-20 bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-brand-600 h-3 rounded-full transition-all duration-300"
                      style={{width: `${((roleData.count || 0) / (statistics.totalUsers || 1)) * 100}%`}}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Summary */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-brand-200 rounded-xl">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-brand-100 rounded-full flex-shrink-0">
            <Users className="w-6 h-6 text-brand-600" />
          </div>
          <div className="space-y-2">
            <AppSectionTitle className="text-brand-900">
              ملخص حالة النظام
            </AppSectionTitle>
            <AppText className="text-brand-700 leading-relaxed">
              النظام يحتوي على <span className="font-bold">{statistics.totalUsers || 0}</span> مستخدم،
              <span className="font-bold">{statistics.activeUsers || 0}</span> منهم نشط (<span className="font-bold">{statistics.activePercentage || 0}%</span>)،
              و <span className="font-bold">{statistics.marketDevelopers || 0}</span> مسوق سوق.
            </AppText>
          </div>
        </div>
      </Card>
    </div>
  );
};
